export type SportModuleState =
  | 'disabled'
  | 'coming_soon'
  | 'beta'
  | 'public'
  | 'admin_only';

export interface SportModuleCatalogItem {
  id: string;
  label: string;
  category: string;
  sortOrder: number;
}

export interface SportModuleCell {
  state: SportModuleState;
  visibilityNotes?: string | null;
  updatedAt?: string;
}

export interface SportModuleMatrixData {
  version: string;
  modules: SportModuleCatalogItem[];
  sports: string[];
  matrix: Record<string, Record<string, SportModuleCell>>;
  manifestPath?: string;
}

export const SPORT_PUBLIC_TAB_MODULES = [
  { id: 'rubric', label: 'Rubric' },
  { id: 'learning', label: 'Learning' },
  { id: 'ranking', label: 'Player Rankings' },
  { id: 'contests', label: 'Contest' },
] as const;

export type SportPublicTabId = (typeof SPORT_PUBLIC_TAB_MODULES)[number]['id'];

export const STATE_RANK: Record<SportModuleState, number> = {
  disabled: 0,
  coming_soon: 1,
  admin_only: 2,
  beta: 3,
  public: 4,
};

export const STATE_LABELS: Record<SportModuleState, string> = {
  disabled: 'Disabled',
  coming_soon: 'Coming soon',
  beta: 'Beta',
  public: 'Public',
  admin_only: 'Admin only',
};

export const STATE_COLORS: Record<SportModuleState, 'default' | 'warning' | 'info' | 'success' | 'secondary'> = {
  disabled: 'default',
  coming_soon: 'warning',
  beta: 'info',
  public: 'success',
  admin_only: 'secondary',
};

export interface ModuleAccessContext {
  isAuthenticated: boolean;
  isAdmin: boolean;
}

export function getModuleState(
  data: SportModuleMatrixData | null | undefined,
  sport: string,
  moduleId: string
): SportModuleState {
  return data?.matrix?.[sport]?.[moduleId]?.state ?? 'disabled';
}

export function isTabVisible(state: SportModuleState, ctx: ModuleAccessContext): boolean {
  if (state === 'disabled') return false;
  if (state === 'admin_only') return ctx.isAdmin;
  return true;
}

export function isContentEnabled(state: SportModuleState, ctx: ModuleAccessContext): boolean {
  if (state === 'disabled' || state === 'coming_soon') return false;
  if (state === 'admin_only') return ctx.isAdmin;
  if (state === 'beta') return ctx.isAuthenticated || ctx.isAdmin;
  return true;
}

export function getVisiblePublicTabs(
  data: SportModuleMatrixData | null | undefined,
  sport: string,
  ctx: ModuleAccessContext
) {
  return SPORT_PUBLIC_TAB_MODULES.filter((tab) =>
    isTabVisible(getModuleState(data, sport, tab.id), ctx)
  );
}

/** Highest public-tab readiness for landing cards */
export function getSportReadinessSummary(
  data: SportModuleMatrixData | null | undefined,
  sport: string
): { label: string; state: SportModuleState } {
  const tabs = SPORT_PUBLIC_TAB_MODULES.map((t) => getModuleState(data, sport, t.id));
  if (tabs.includes('public')) return { label: 'Live modules', state: 'public' };
  if (tabs.includes('beta')) return { label: 'Beta preview', state: 'beta' };
  if (tabs.includes('coming_soon')) return { label: 'Coming soon', state: 'coming_soon' };
  if (tabs.includes('admin_only')) return { label: 'In development', state: 'admin_only' };
  return { label: 'Planned', state: 'disabled' };
}

export function countPublicModules(
  data: SportModuleMatrixData | null | undefined,
  sport: string
): number {
  return SPORT_PUBLIC_TAB_MODULES.filter(
    (t) => getModuleState(data, sport, t.id) === 'public'
  ).length;
}
